import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { Constants } from 'app/core/constant/app-constant';
import { RoleList } from 'app/core/enums/role-list.enum';
import { ToasterService } from 'app/core/services/toaster.service';
import { UserService } from 'app/shared/services/user.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Profile Completion Guard
 *
 * Checks if filmmaker has completed their profile.
 * If profile is incomplete, redirects to signup page with mode=complete-profile.
 *
 * Usage: Apply to filmmaker routes that require complete profile
 */
export const ProfileCompletionGuard: CanActivateFn = (
    route,
    state
): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const userService = inject(UserService);
    const toasterService = inject(ToasterService);

    // If profile completion is not mandatory, allow access without checking
    if (!Constants.IS_PROFILE_COMPLETION_MANDATORY) {
        return of(true);
    }

    // Get current user role
    const currentRole = authService.currentUserRoleSubject.value;

    // Only check profile completion for filmmakers
    if (currentRole !== RoleList.FilmMaker) {
        return of(true); // Not a filmmaker, allow access
    }

    // Check if already on profile completion page to prevent redirect loops
    if (
        state.url.includes('/profile-completion') ||
        state.url.includes('/sign-up')
    ) {
        return of(true); // Already on profile completion or signup page, allow access
    }

    // Check profile completion status
    const countryCode = authService.currentUserValue?.countryCode;
    return userService.getProfile(countryCode).pipe(
        map((profile) => {
            // Check if profile is complete
            if (
                profile.isFilmmakerProfileCompleted === true ||
                profile.isFilmmakerProfileCompleted == undefined
            ) {
                return true; // Profile complete, allow access
            }

            // Profile incomplete, redirect to profile completion page
            toasterService.displaySnackBar(
                'Please complete your profile to continue',
                'warning'
            );

            return router.createUrlTree(['/profile-completion']);
        }),
        catchError((error) => {
            console.error(
                'ProfileCompletionGuard: Error checking profile completion',
                error
            );
            // On error, allow access to prevent blocking user
            return of(true);
        })
    );
};
